import { useEffect } from 'preact/hooks';
import { OfferImage } from 'components/offer/card/OfferImage';
import { localize } from 'i18n/localize.ts';
import { price } from 'utils/format.tsx';
import { generateUnpackedOfferDetailLink } from 'utils/url.ts';
import { cn } from 'utils/cn';
import type { UnpackedOfferData } from 'service/unpacked/model.ts';
import 'components/offer/card/OfferCard.less';
import { Badge } from 'components/atoms/Badge.tsx';
import { Icon } from 'components/atoms/Icon.tsx';
import { Tooltip } from 'components/tooltip/Tooltip.tsx';
import { user } from 'signals/login';
import './unpackedOfferCard.less';
import { DOTHelper } from 'lib/dot/DOTHelper.ts';
import { useSignals } from 'context/SignalsContext.tsx';
import { filters as globalFilters } from 'signals/filters';
import { localFiltersToAnalyticsFilters } from 'lib/dot/utils.ts';

const $t = localize();

const SBR_LABEL_PREFIX = 'zbozi-sbr-';

export interface Props {
	analyticsPosition: string;
	lazyImage?: boolean;
	showUnpackedBadge?: boolean;
	unpackedOffer: UnpackedOfferData;
}

/**
 * For easier styling this component relies on tailwind and also on its sibling .less file for nested CSS selectors.
 */
export default function UnpackedOfferCard({
	analyticsPosition,
	lazyImage,
	showUnpackedBadge = true,
	unpackedOffer,
}: Props) {
	const { filters = globalFilters } = useSignals();
	const isUserWithSbr =
		user.value.state !== 'uninitialized' && globalThis?.window ? !!globalThis.window.login.current.sbr : false;
	useEffect(() => {
		if (isUserWithSbr) {
			const sbrLabelElement = globalThis.document.getElementById(`${SBR_LABEL_PREFIX}${unpackedOffer.id}`);
			if (sbrLabelElement && typeof globalThis.window.sssp?.appendCoopIconToElement === 'function') {
				sbrLabelElement.innerHTML = '';
				globalThis.window.sssp.appendCoopIconToElement(
					{ align: 'left', paid: 'Seznam.cz a.s.', behalf: 'Seznam.cz a.s.' },
					sbrLabelElement,
				);
			}
		}
	}, [isUserWithSbr]);

	return (
		<a
			class="group/card"
			href={generateUnpackedOfferDetailLink(unpackedOffer)}
			onClick={() => {
				const _filters = filters.peek();
				const searchType = DOTHelper.getStore().searchType;

				DOTHelper.setOfferDetailMeta({
					...(_filters && { filters: localFiltersToAnalyticsFilters(_filters) }),
					...(searchType && { searchType }),
					advertPosition: analyticsPosition,
				});
			}}
		>
			<div class="offer-card @container w-full h-full relative">
				<div
					class={cn(
						'relative offer-card__image-wrapper rounded-2xl bg-gray-card flex items-center justify-center overflow-hidden',
					)}
				>
					<OfferImage
						alt={unpackedOffer.name}
						className="group-hover/card:scale-105 transition-all duration-500"
						images={unpackedOffer.images.map((img) => ({ url: img }))}
						lazy={lazyImage}
					/>
				</div>
				<div class="absolute top-0 w-full aspect-square">
					{showUnpackedBadge && (
						<div class="absolute z-100 bottom-2 left-2 @cardMd:bottom-3 @cardMd:left-3 @cardLg:bottom-4 @cardLg:left-4">
							<Tooltip
								placement={'top-right'}
								containerClassName="px-6 py-8 md:py-2 md:px-3 md:w-[220px] left-0 md:left-0 md:bottom-10"
								mobileTitle={$t.unpackedOffer.label}
								openerContent={
									<Badge>
										<span class="flex-shrink-0">{$t.unpackedOffer.label}</span>{' '}
										{isUserWithSbr ? (
											<span
												className="flex items-center h-[19.2px] sbr-zbozi-label"
												id={`${SBR_LABEL_PREFIX}${unpackedOffer.id}`}
												onClick={(event) => {
													event.stopPropagation();
												}}
											/>
										) : (
											<Icon
												type={'info'}
												size={18}
												className="ml-1 text-dark-blue-25"
												fill={true}
											/>
										)}
									</Badge>
								}
							>
								<span className="text-sm">{$t.unpackedOffer.tooltip}</span>
							</Tooltip>
						</div>
					)}
				</div>
				<div class="py-3 @cardLg:px-2 pb-0">
					<div class="text-red group-hover/card:text-red-hover line-clamp-2 overflow-ellipsis leading-6 text-left">
						{unpackedOffer.name}
					</div>
					<div class="mt-1 line-clamp-1 overflow-ellipsis text-dark-blue-50 flex justify-between">
						<b class="text-neutral-black flex-none">{price(unpackedOffer.price)}</b>
						&nbsp;
						<span class="whitespace-pre text-dark-blue-50 overflow-ellipsis shrink group-hover/card:text-dark-blue truncate">
							{unpackedOffer.shop.name}
						</span>
					</div>
				</div>
			</div>
		</a>
	);
}
